import React from "react";

//MUI
import { Card, Typography, Button, Stack, Box } from "@mui/material";

//Icons
import {
  Place as LocationIcon,
  CalendarToday as DateIcon,
  ChevronRight as BackIcon,
} from "@mui/icons-material";

// Styles
import { styles } from "../styles";

// Libraries
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

// Components
import { getLocalizedTime, getLocalizedDate } from "Components/Language/Common";
import { MNLink, InfoRow } from "Common/Components";
import PanAmNote from "../Common/PanAmNote";

// Constants
import { APPOINTMENT_AVAILABILITY_STATUS } from "Constants/AppointmentConstants";
const { ONLINE_AVAILABLE, WALK_IN_AVAILABLE, FULLY_BOOKED } =
  APPOINTMENT_AVAILABILITY_STATUS;

const ClinicCard = ({
  clinic,
  groupingTitle,
  createAppointment,
  handleViewMoreModal,
  isTimeSelected,
  isMBS = false,
  today,
}) => {
  const { t } = useTranslation();
  const displayedDate = DateTime.fromISO(groupingTitle).isValid
    ? DateTime.fromISO(groupingTitle).toISODate()
    : today;
  const dateSlots = clinic.availableSlots.filter(
    (slot) => slot.date === groupingTitle
  );
  const maxSlots = isTimeSelected ? 6 : 5;

  return (
    <Card key={clinic.id} sx={styles.clinicCard}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack alignItems="flex-start" width="100%" pb={{ xs: 2, sm: 0 }}>
          <Typography sx={styles.clinicName}>
            {t("clinic.name", { clinic: clinic.Clinic })}
          </Typography>
          <InfoRow
            IconComponent={LocationIcon}
            text={t("clinic.address", { clinic: clinic.Clinic })}
          />
          <InfoRow
            IconComponent={DateIcon}
            text={getLocalizedDate(displayedDate, "cccc, MMMM dd, yyyy")}
          />
          {clinic.Clinic?.hoursOfOperationUrl && (
            <MNLink
              title={t("clinic.detailsURL")}
              link={clinic.Clinic.hoursOfOperationUrl}
            />
          )}
        </Stack>
        {clinic.availabilityStatus[displayedDate] === ONLINE_AVAILABLE &&
          dateSlots.length > 0 && (
            <Box sx={styles.timeSlotGrid}>
              {dateSlots.slice(0, maxSlots).map((slot, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  sx={{ width: "100px" }}
                  onClick={() => createAppointment(clinic, displayedDate, slot)}
                >
                  {getLocalizedTime(slot.startTime)}
                </Button>
              ))}
              {!isTimeSelected && (
                <Button
                  variant="contained"
                  sx={{ width: "100px" }}
                  onClick={() => handleViewMoreModal(clinic, displayedDate)}
                  disabled={dateSlots.length <= 5}
                  endIcon={<BackIcon />}
                >
                  {t("buttons.more")}
                </Button>
              )}
            </Box>
          )}
        {clinic.availabilityStatus[displayedDate] === WALK_IN_AVAILABLE && (
          <Stack
            sx={styles.rightCardStackBase}
            alignItems={{ xs: "center", sm: "flex-end" }}
          >
            <Typography sx={styles.availabilityText}>
              {t("availableAppointments.acceptingWalkIns")}
            </Typography>
          </Stack>
        )}
        {clinic.availabilityStatus[displayedDate] === FULLY_BOOKED && (
          <Stack
            sx={styles.rightCardStackBase}
            alignItems={{ xs: "center", sm: "flex-end" }}
          >
            <Typography sx={styles.availabilityText}>
              {t("availableAppointments.fullyBooked")}
            </Typography>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              textAlign={{ xs: "center", sm: "right" }}
            >
              {t("availableAppointments.fullyBookedHelper")}
            </Typography>
          </Stack>
        )}
      </Stack>
      {clinic.availabilityStatus[displayedDate] !== FULLY_BOOKED && (
        <PanAmNote clinicId={clinic.id} isMBS={isMBS} />
      )}
    </Card>
  );
};

export default ClinicCard;
